/*
 * Тексты карточек разговора, выбора модели и кольца памяти (006: T085, T089, T109, T112; contracts/ui.md §2–§3; FR-028,
 * FR-033, FR-037, FR-043). Часть общего словаря `texts.ts`: он отдаёт `CARD_TEXTS` дальше.
 *
 * Язык — ASD-STE100: короткие предложения, условие в начале, одно слово — одно значение, нейтральный род, система не
 * говорит «я». Слов спецификации «тред», «ход», «контекст», «вмешательство», имён инструментов и кодов здесь нет
 * («Слова интерфейса» spec.md). Что сделает агент, почему спрашиваем и что будет после ответа — тексты сервера
 * (`api/src/modules/agent-thread/cards.ts`); названия правил и моделей, состав проекта и причина «устарел» — тоже с
 * сервера. Проверяет `texts-cards.test.tsx`.
 *
 * Разделы: `label` — заголовки и подписи (до 6 слов), `text` — предложения, `say` — тексты с подстановкой, `history` и
 * `effort` — подписи по значению.
 */

/** Подписи и заголовки — до 6 слов. */
const label = {
  /* Запрос агента */
  approvalTitle: 'Агент просит разрешения',
  questionTitle: 'Агент задаёт вопрос',
  waiting: 'ждёт ответа',
  why: 'Почему спрашиваем',
  afterAllow: 'После «Разрешить»',
  afterDeny: 'После «Запретить»',
  deny: 'Запретить',
  allowThread: 'Разрешить в этом разговоре',
  allowOnce: 'Разрешить один раз',
  skip: 'Не отвечать',
  answer: 'Отправить ответ',
  sending: 'Отправка…',
  options: 'Варианты ответа',
  freeAnswer: 'Ваш ответ',
  errorCode: 'Код ошибки:',

  /* «Меняет черновик» */
  draftTitle: 'Меняет черновик',
  draftBadge: 'черновик',
  was: 'было',
  will: 'станет',

  /* Проект новой версии правил */
  projectReady: 'Готов',
  projectPublished: 'Опубликован',
  projectStale: 'Устарел',
  projectChanges: 'Что изменится',
  openProject: 'Открыть проект',

  /* Модель и рассуждение */
  modelTitle: 'Модель и рассуждение',
  model: 'Модель',
  effort: 'Рассуждение',
  defaultModel: 'Модель по умолчанию',
  isDefault: 'по умолчанию',
  fallbackShort: 'запасной список',
  loadingModels: 'Загрузка моделей…',
  findModel: 'Найти модель',
  reloadModels: 'Загрузить список снова',
  close: 'Закрыть',

  /* Память разговора */
  memory: 'Память разговора',
  memoryShort: 'Память',
} as const;

/** Предложения. */
const text = {
  deadline: 'Агент ждёт вашего ответа. Если ответа не будет 25 минут, запрос отменится.',
  sent: 'Ответ отправлен. Агент продолжит работу.',
  answerFailed: 'Ответ не отправлен.',
  pickOption: 'Выберите вариант ответа.',
  freePlaceholder: 'Например: да, с 01.11.2026',

  draftNotPublished: 'Правила пока не опубликованы.',
  projectWho: 'Публикует человек на странице правил. Агент не публикует.',

  modelNote: 'Список дал агент. Новая модель работает со следующего ответа.',
  modelsFallback: 'Список моделей не загрузился. Показан запасной список.',
  modelsStale: 'Компьютер давно не отвечал. Список моделей может быть старым.',
  modelsFailed: 'Список моделей не загрузился.',
  modelNotChanged: 'Модель не сменилась.',
  modelNotFound: 'Такой модели нет в списке. Измените запрос.',

  memoryHint:
    'Разговор занимает место в памяти агента. Когда память заполнена на 90 %, начните новый разговор.',
} as const;

/**
 * Строка истории запроса (contracts/thread-events.md §5, `state`): бейдж. «Отменено» — запрос закрыт без ответа
 * (остановка, обрыв связи, отключение компьютера).
 */
const history = {
  waiting: 'Ждёт ответа',
  allowed_once: 'Разрешено один раз',
  allowed_thread: 'Разрешено в этом разговоре',
  denied: 'Запрещено',
  cancelled: 'Отменено',
  expired: 'Срок ответа прошёл',
  answered: 'Ответ дан',
} as const;

/** Кто закрыл запрос (`by` строки ленты): предложение после бейджа. */
const historyBy = {
  allowed: 'Вы разрешили.',
  denied: 'Вы запретили.',
  answered: 'Вы ответили.',
  threadAllow: 'Без вопроса: правку черновика вы разрешили в этом разговоре.',
  cancelled: 'Запрос закрыт без ответа.',
  expired: 'Ответа не было 25 минут.',
} as const;

/**
 * Уровень рассуждения от агента словами. Значения — из списка агента (Claude, Codex, pi); незнакомое значение
 * показывается как есть.
 */
const effort: Readonly<Record<string, string>> = {
  off: 'без рассуждения',
  none: 'без рассуждения',
  minimal: 'минимальное',
  low: 'низкое',
  medium: 'среднее',
  high: 'высокое',
  xhigh: 'очень высокое',
  max: 'максимальное',
};

/** Тексты с подстановкой. Даты — уже «дд.мм.гггг», время — «дд.мм.гггг чч:мм» по времени объекта. */
const say = {
  /** «Правило «Обед», окно обеда:» — подпись строки «было → станет». */
  draftItem: (rule: string, field: string) => `Правило «${rule}», ${field}:`,
  draftValidFrom: (date: string) => `Новые правила начнут действовать с ${date}.`,
  projectReady: (n: number) => `Проект новой версии правил №${n} готов`,
  projectPublished: (n: number) => `Проект новой версии правил №${n} опубликован`,
  /** FR-033: устаревший проект — «Проект №N устарел» и причина (текст сервера). */
  projectStale: (n: number) => `Проект №${n} устарел`,
  projectValidFrom: (date: string) => `Новые правила начнут действовать с ${date}.`,
  /** Флажки перед публикацией (`needs` сервера): «Перед публикацией нужно: подтвердить проверочные примеры». */
  projectNeeds: (needs: string) => `Перед публикацией нужно: ${needs}.`,
  projectUntil: (when: string) => `Опубликовать проект можно до ${when}.`,
  /** Кнопка модели: «Модель Б · среднее». */
  modelButton: (model: string, effortText: string | null) =>
    effortText ? `${model} · ${effortText}` : model,
  memory: (percent: number) => `${label.memory} ${percent} %`,
  memoryShort: (percent: number) => `${label.memoryShort} ${percent} %`,
};

export const CARD_TEXTS = { label, text, history, historyBy, effort, say } as const;
